/**
 * /settings/tokens — mint + revoke API tokens for MCP and external agents.
 *
 * The picker below lets the user compose the scopes that get serialized
 * into `api_tokens.scopes`. Available permissions come from
 * `@friday/shared/permissions`. The default permission set is tuned to
 * "just enough for an MCP agent": read + write tasks, comments, sprints,
 * labels. Admin / delete / member-write are NOT on by default; the user
 * has to explicitly opt in.
 */

import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { ApiToken, ApiTokenScope } from '@friday/shared/realtime'
import { PERMISSIONS, type Permission } from '@friday/shared/permissions'
import { cn } from '@/lib/utils'

const DEFAULT_PERMISSIONS: Permission[] = [
  'task:read',
  'task:write',
  'comment:write',
  'sprint:read',
  'sprint:write',
  'label:read',
  'label:write',
]

export function ApiTokensPage() {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [permissions, setPermissions] = useState<Set<Permission>>(
    () => new Set(DEFAULT_PERMISSIONS),
  )
  const [denied, setDenied] = useState<Set<Permission>>(
    () => new Set<Permission>(),
  )
  const [rateLimit, setRateLimit] = useState(60)
  const [expiresInDays, setExpiresInDays] = useState<number | null>(null)
  const [justCreated, setJustCreated] = useState<{ id: string; plaintext: string } | null>(null)

  const tokens = useQuery({
    queryKey: ['me', 'api-tokens'],
    queryFn: () => api<ApiToken[]>('/api/v1/me/api-tokens'),
  })

  const create = useMutation({
    mutationFn: () => {
      // Always mint a workspace-wide token; the user picked the permissions
      // and (optionally) explicit denies. Resource scope "all my workspaces"
      // covers MCP use cases without making the UI carry a workspace picker
      // that has nothing to do with the actual problem.
      const scopes: ApiTokenScope[] = [{ kind: 'all-workspaces', role: 'member' }]
      const allowed = [...permissions]
      const deniedList = [...denied]
      if (allowed.length) scopes.push({ kind: 'allow', permissions: allowed })
      if (deniedList.length) scopes.push({ kind: 'deny', permissions: deniedList })
      scopes.push({ kind: 'rate', perMinute: rateLimit })

      const body: { name: string; scopes: ApiTokenScope[]; expiresAt?: string } = {
        name: (name.trim() || 'Untitled token').slice(0, 60),
        scopes,
      }
      if (expiresInDays && expiresInDays > 0) {
        body.expiresAt = new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000).toISOString()
      }
      return api<{ id: string; plaintext: string }>('/api/v1/me/api-tokens', {
        method: 'POST',
        body,
      })
    },
    onSuccess: (t) => {
      setJustCreated(t)
      setName('')
      void qc.invalidateQueries({ queryKey: ['me', 'api-tokens'] })
    },
    onError: (e) => {
      // surface inline; toast would also work
      console.error('Token create failed', e)
    },
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api(`/api/v1/me/api-tokens/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me', 'api-tokens'] }),
  })

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header>
        <Link to="/settings" className="text-xs text-surface-500 hover:text-brand-500">
          ← Settings
        </Link>
        <h1 className="mt-1 text-2xl font-semibold">API tokens</h1>
        <p className="mt-1 text-sm text-surface-500">
          Mint a token for an AI agent (Claude Code via MCP) or an external
          integration. The plaintext is shown once; copy it now.
        </p>
      </header>

      {/* Create form */}
      <section className="card p-5">
        <h2 className="mb-4 text-sm font-semibold">New token</h2>

        <div className="space-y-4">
          <Field label="Token name">
            <input
              className="input"
              placeholder="e.g. Claude Code on my laptop"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
            />
          </Field>

          <Field label="Permissions" hint="What this token can do. Toggle the checkboxes; the buttons on the right explicitly deny a permission even if it's checked.">
            {/* Shortcut for a full-access MCP token. The token still cannot do
                more than the user's own workspace and project roles allow. */}
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn-ghost min-h-9 text-xs"
                disabled={permissions.size === PERMISSIONS.length && denied.size === 0}
                onClick={() => {
                  setPermissions(new Set(PERMISSIONS))
                  setDenied(new Set<Permission>())
                }}
              >
                Select all permissions
              </button>
              <button
                type="button"
                className="btn-ghost min-h-9 text-xs"
                disabled={permissions.size === 0}
                onClick={() => setPermissions(new Set<Permission>())}
              >
                Clear
              </button>
              <span className="text-xs text-surface-500">
                {permissions.size} of {PERMISSIONS.length} selected
              </span>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {PERMISSIONS.map((p) => {
                const allowed = permissions.has(p)
                const deniedHere = denied.has(p)
                return (
                  <div
                    key={p}
                    className="flex items-center gap-2 rounded-md border border-surface-200 dark:border-surface-700 bg-surface-50 dark:bg-surface-800 px-2.5 py-1.5 text-xs text-surface-900 dark:text-surface-100"
                  >
                    <label className="flex flex-1 items-center gap-2">
                      <input
                        type="checkbox"
                        checked={allowed}
                        onChange={(e) => {
                          const next = new Set(permissions)
                          if (e.target.checked) next.add(p)
                          else next.delete(p)
                          setPermissions(next)
                          if (e.target.checked) {
                            const nd = new Set(denied)
                            nd.delete(p)
                            setDenied(nd)
                          }
                        }}
                      />
                      <span className="font-mono">{p}</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const nd = new Set(denied)
                        if (deniedHere) nd.delete(p)
                        else {
                          nd.add(p)
                          const np = new Set(permissions)
                          np.delete(p)
                          setPermissions(np)
                        }
                        setDenied(nd)
                      }}
                      className={cn(
                        'rounded px-2 py-0.5 text-[10px] font-medium',
                        deniedHere
                          ? 'bg-red-500/10 text-red-700 dark:text-red-300'
                          : 'text-surface-500 hover:text-red-600',
                      )}
                      title="Explicitly deny this permission"
                    >
                      {deniedHere ? 'Denied' : 'Deny'}
                    </button>
                  </div>
                )
              })}
            </div>
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Rate limit">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={1000}
                  className="input max-w-[8rem]"
                  value={rateLimit}
                  onChange={(e) =>
                    setRateLimit(Math.max(1, Math.min(1000, Number(e.target.value) || 1)))
                  }
                />
                <span className="text-xs text-surface-500">requests / minute</span>
              </div>
            </Field>
            <Field label="Expires in (days)" hint="0 = never expires">
              <input
                type="number"
                min={0}
                max={365}
                className="input max-w-[8rem]"
                value={expiresInDays ?? 0}
                onChange={(e) => {
                  const v = Number(e.target.value)
                  setExpiresInDays(v > 0 ? v : null)
                }}
              />
            </Field>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              className="btn-primary"
              disabled={create.isPending || permissions.size === 0}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Creating…' : 'Create token'}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setName('')
                setPermissions(new Set(DEFAULT_PERMISSIONS))
                setDenied(new Set<Permission>())
                setRateLimit(60)
                setExpiresInDays(null)
              }}
            >
              Reset
            </button>
            {create.error ? (
              <span className="text-xs text-red-600">
                {(create.error as Error).message ?? 'Failed to create'}
              </span>
            ) : null}
          </div>
        </div>

        {justCreated && (
          <div className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
            <div className="mb-1 font-medium text-amber-700 dark:text-amber-300">
              New token (shown once)
            </div>
            <code className="block break-all rounded bg-amber-500/10 p-2 font-mono">
              {justCreated.plaintext}
            </code>
            <button
              className="mt-2 text-amber-700 dark:text-amber-300 hover:underline"
              onClick={() => {
                navigator.clipboard?.writeText(justCreated.plaintext)
              }}
            >
              Copy to clipboard
            </button>
          </div>
        )}
      </section>

      {/* Existing tokens */}
      <section className="card divide-y divide-surface-200 dark:divide-surface-700">
        <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-surface-500">
          Active tokens
        </div>
        {(tokens.data ?? []).map((t) => (
          <div key={t.id} className="flex items-center justify-between px-4 py-3 text-sm">
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{t.name}</div>
              <div className="truncate text-xs text-surface-500">
                {summarizeScopes(t.scopes)} · Created {new Date(t.createdAt).toLocaleString()} ·{' '}
                {t.revokedAt
                  ? 'Revoked'
                  : t.lastUsedAt
                    ? `Last used ${new Date(t.lastUsedAt).toLocaleString()}`
                    : 'Never used'}
                {t.expiresAt ? ` · Expires ${new Date(t.expiresAt).toLocaleDateString()}` : ''}
              </div>
            </div>
            {!t.revokedAt && (
              <button
                onClick={() => {
                  if (confirm(`Revoke "${t.name}"? Anything using it will lose access.`)) {
                    revoke.mutate(t.id)
                  }
                }}
                className="btn-ghost text-red-600 dark:text-red-300"
              >
                Revoke
              </button>
            )}
          </div>
        ))}
        {tokens.data?.length === 0 && (
          <div className="p-6 text-center text-sm text-surface-500">No tokens yet.</div>
        )}
      </section>

      <p className="text-xs text-surface-500">
        Need to test? Try the{' '}
        <a
          className="text-brand-600 hover:underline"
          href="https://mcp.friday.thriv.es"
          target="_blank"
          rel="noreferrer"
        >
          MCP server
        </a>{' '}
        with the token above.
      </p>
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-surface-600 dark:text-surface-300">
        {label}
      </span>
      {hint ? (
        <span className="mt-0.5 block text-[10px] text-surface-500">{hint}</span>
      ) : null}
      <div className="mt-1">{children}</div>
    </label>
  )
}

function summarizeScopes(scopes: unknown): string {
  if (!Array.isArray(scopes)) return 'no scopes'
  const parts: string[] = []
  for (const s of scopes) {
    if (!s || typeof s !== 'object') continue
    const k = (s as { kind?: string }).kind
    if (k === 'all-workspaces') parts.push('all-ws')
    if (k === 'workspace') parts.push('one-ws')
    if (k === 'project') parts.push('one-project')
    if (k === 'allow') {
      const n = Array.isArray((s as { permissions?: unknown }).permissions) ? (s as { permissions: unknown[] }).permissions.length : 0
      parts.push(`+${n}`)
    }
    if (k === 'deny') {
      const n = Array.isArray((s as { permissions?: unknown }).permissions) ? (s as { permissions: unknown[] }).permissions.length : 0
      parts.push(`−${n}`)
    }
    if (k === 'rate') parts.push(`${(s as { perMinute?: number }).perMinute ?? '?'}/min`)
  }
  return parts.join(' · ') || 'no scopes'
}